/*
 * <hero-editorial> — full-bleed editorial hero carousel.
 * Auto-advances on a timer (configurable via data-rotate-ms).
 * Pauses on hover/focus. Drag/swipe ±60px to advance manually.
 * Indicator dot clicks jump to a specific slide.
 * Respects prefers-reduced-motion (no auto-advance).
 *
 * Wrapped in IIFE + customElements.get guard in case the script ever
 * gets included twice on a page.
 */
(function () {
  if (customElements.get('hero-editorial')) return;

  class HeroEditorial extends HTMLElement {
    constructor() {
      super();
      this.slides = [];
      this.dots = [];
      this.currentEl = null;
      this.index = 0;
      this.rotateMs = 6000;
      this.timerId = null;
      this.paused = false;
      this.reducedMotion = false;
      this.boundAdvance = () => this.advance(1);

      // Drag state
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragMoved = 0;
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.hero-editorial__slide'));
      if (this.slides.length === 0) return;

      this.dots = Array.from(this.querySelectorAll('.hero-editorial__dot'));
      this.currentEl = this.querySelector('[data-hero-current]');
      this.rotateMs = parseInt(this.dataset.rotateMs, 10) || 6000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.bind();
      if (!this.reducedMotion && this.slides.length > 1) this.startTimer();
    }

    disconnectedCallback() {
      this.stopTimer();
      this.unbind();
    }

    bind() {
      this.onEnter = () => { this.paused = true; this.stopTimer(); };
      this.onLeave = () => {
        this.paused = false;
        if (!this.reducedMotion && !this.isDragging) this.startTimer();
      };
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onVisibility = () => {
        if (document.hidden) this.stopTimer();
        else if (!this.paused && !this.reducedMotion) this.startTimer();
      };

      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);

      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('dragstart', function (e) { e.preventDefault(); });

      this.dots.forEach((d) => d.addEventListener('click', this.onDotClick));

      document.addEventListener('visibilitychange', this.onVisibility);

      if (!this.reducedMotion) {
        this._rafId = null;
        this._scheduleParallax = () => {
          if (this._rafId) return;
          this._rafId = requestAnimationFrame(() => {
            this._rafId = null;
            this._updateParallax();
          });
        };
        window.addEventListener('scroll', this._scheduleParallax, { passive: true });
        this._updateParallax();
      }
    }

    unbind() {
      this.dots.forEach((d) => d.removeEventListener('click', this.onDotClick));
      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      document.removeEventListener('pointercancel', this.onPointerUp);
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this._scheduleParallax) {
        window.removeEventListener('scroll', this._scheduleParallax);
        if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null; }
      }
    }

    _updateParallax() {
      const rect = this.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      const offset = ((progress * 2) - 1) * this.offsetHeight * 0.1;
      this.querySelectorAll('.hero-editorial__image').forEach((img) => {
        img.style.transform = `translateY(${offset.toFixed(1)}px)`;
      });
    }

    startTimer() {
      this.stopTimer();
      this.timerId = window.setInterval(this.boundAdvance, this.rotateMs);
    }

    stopTimer() {
      if (this.timerId) {
        window.clearInterval(this.timerId);
        this.timerId = null;
      }
    }

    advance(delta) {
      const next = (this.index + delta + this.slides.length) % this.slides.length;
      this.goto(next);
    }

    goto(targetIndex) {
      if (targetIndex === this.index) return;
      const prev = this.slides[this.index];
      const next = this.slides[targetIndex];
      if (!next) return;

      prev.classList.remove('hero-editorial__slide--active');
      prev.setAttribute('aria-hidden', 'true');
      next.classList.add('hero-editorial__slide--active');
      next.setAttribute('aria-hidden', 'false');

      if (this.dots[this.index]) {
        this.dots[this.index].classList.remove('hero-editorial__dot--active');
        this.dots[this.index].setAttribute('aria-selected', 'false');
      }
      if (this.dots[targetIndex]) {
        this.dots[targetIndex].classList.add('hero-editorial__dot--active');
        this.dots[targetIndex].setAttribute('aria-selected', 'true');
      }

      this.index = targetIndex;

      if (this.currentEl) {
        this.currentEl.textContent = ('0' + (this.index + 1)).slice(-2);
      }

      // Reset the timer so the user gets the full duration on a new slide.
      if (this.timerId) this.startTimer();
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      // Ignore drags that start on links/buttons — those should fire their click.
      if (event.target.closest('a, button')) return;

      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragMoved = 0;
      this.stopTimer();
      document.addEventListener('pointermove', this.onPointerMove);
      document.addEventListener('pointerup', this.onPointerUp);
      document.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.isDragging) return;
      this.dragMoved = event.clientX - this.dragStartX;
    }

    onPointerUp() {
      if (!this.isDragging) return;
      this.isDragging = false;
      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      document.removeEventListener('pointercancel', this.onPointerUp);

      // Threshold: 60px swipe advances ±1 slide
      if (this.dragMoved <= -60) this.advance(1);
      else if (this.dragMoved >= 60) this.advance(-1);

      this.dragMoved = 0;
      if (!this.paused && !this.reducedMotion) this.startTimer();
    }

    onDotClick(event) {
      const target = parseInt(event.currentTarget.dataset.slideTarget, 10);
      if (!isNaN(target)) this.goto(target);
    }

    onKey(event) {
      if (event.key === 'ArrowLeft') { this.advance(-1); event.preventDefault(); }
      else if (event.key === 'ArrowRight') { this.advance(1); event.preventDefault(); }
    }
  }

  customElements.define('hero-editorial', HeroEditorial);
})();
